import type { ReactNode } from 'react';

import {
  Button,
  Flex,
  Icon,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type IconType,
} from '@kognitos/lattice';

import { ClampedText } from '@/shared/components/ClampedText';

/** One tab of an asset document, e.g. Steps, Map, Schema or Data sync. */
export interface IAssetDocumentTab {
  value: string;
  label: string;
  icon?: IconType;
  'data-testid'?: string;
}

/**
 * Props for {@link AssetDocument}.
 */
export interface IAssetDocumentProps {
  /** The asset's icon, shown in a tile before the title. */
  icon?: IconType;
  /** The asset's name. A node so a builder can make it editable. */
  title: ReactNode;
  /** After the title: the stage badge and anything else about its state. */
  badges?: ReactNode;
  /** At the end of the title bar, e.g. "Last saved 4:16 PM" or the live run state. */
  meta?: ReactNode;
  /** The version history button at the end of the title bar. Omit to hide it. */
  versionHistory?: {
    /** Its tooltip and accessible name, e.g. "Version history (v4)". */
    label: string;
    onClick: () => void;
    'data-testid'?: string;
  };
  /** The document's views, on the row under the title bar. Omit for a document with one view. */
  tabs?: {
    items: IAssetDocumentTab[];
    value: string;
    onValueChange: (value: string) => void;
    label: string;
  };
  /** At the very end of the title bar: the asset's `⋮` options menu. */
  menu?: ReactNode;
  /** Right of the tabs: the primary actions, e.g. Run and Publish. */
  actions?: ReactNode;
  description?: ReactNode;
  /** Above the body, e.g. a test-run banner. */
  notice?: ReactNode;
  /** The active view. It owns its own scrolling. */
  children: ReactNode;
  'data-testid'?: string;
}

/**
 * The asset's document in the Definition view: a card with a title bar (the
 * name, its state, and history), a row with the views' tabs and the primary
 * actions, then the active view. Every builder's document has the same title
 * bar and row, so run, history and publish are always in the same place.
 */
export function AssetDocument({
  icon,
  title,
  badges,
  meta,
  versionHistory,
  menu,
  tabs,
  actions,
  description,
  notice,
  children,
  'data-testid': dataTestId = 'asset-document',
}: IAssetDocumentProps) {
  return (
    <Flex
      direction="column"
      width="full"
      height="full"
      overflow="hidden"
      className="min-h-0 min-w-0 rounded-2xl border border-border bg-card shadow-xs"
      data-testid={dataTestId}
    >
      <Flex
        align="center"
        justify="between"
        gap={3}
        width="full"
        paddingX={4}
        className="h-14 shrink-0 border-b border-border/60"
        data-testid="asset-document-title-bar"
      >
        <Flex align="center" gap={2} flex="1" className="min-w-0">
          {icon && (
            <Flex
              align="center"
              justify="center"
              aria-hidden
              className="size-7 shrink-0 rounded-md bg-secondary text-muted-foreground"
            >
              <Icon type={icon} size="sm" />
            </Flex>
          )}
          {title}
          {badges && (
            <Flex align="center" gap={1} className="shrink-0">
              {badges}
            </Flex>
          )}
        </Flex>
        {(meta || versionHistory || menu) && (
          <Flex align="center" gap={2} className="min-w-0 shrink-0">
            {meta}
            {versionHistory && (
              <Tooltip delayDuration={200} variant="primary">
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="rounded-md text-muted-foreground hover:text-foreground"
                    onClick={versionHistory.onClick}
                    aria-label={versionHistory.label}
                    data-testid={
                      versionHistory['data-testid'] ??
                      'asset-document-version-history'
                    }
                  >
                    {/* 13px sits between lattice's size-3 and size-3.5, as on the automation title bar. */}
                    <Icon type="History" className="size-[13px]" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end">
                  {versionHistory.label}
                </TooltipContent>
              </Tooltip>
            )}
            {menu}
          </Flex>
        )}
      </Flex>
      {(tabs || actions) && (
        <Flex
          align="center"
          justify={tabs ? 'between' : 'end'}
          gap={3}
          width="full"
          paddingX={4}
          paddingY={3}
          className="shrink-0"
        >
          {tabs && (
            <Tabs value={tabs.value} onValueChange={tabs.onValueChange}>
              {/* TODO: Use `TabsList variant="ghost"` (`tone="informative"` on a draft) once kognitos/lattice#189 is released, and drop these classes. */}
              <TabsList
                aria-label={tabs.label}
                className="h-auto gap-1 bg-transparent p-0"
              >
                {tabs.items.map(tab => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="asset-document-tab flex-none rounded-md px-3 py-1.5 data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none dark:data-[state=active]:border-transparent dark:data-[state=active]:bg-muted"
                    data-testid={
                      tab['data-testid'] ?? `asset-document-tab-${tab.value}`
                    }
                  >
                    {tab.icon && <Icon type={tab.icon} size="sm" />}
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
          {actions && (
            <Flex
              align="center"
              gap={2}
              className="shrink-0 animate-in duration-200 fade-in zoom-in-95 motion-reduce:animate-none"
              data-testid="asset-document-actions"
            >
              {actions}
            </Flex>
          )}
        </Flex>
      )}
      {description && (
        // Clamped with a Show more, so a long description is never cut off.
        <ClampedText
          lines={2}
          contentKey={description}
          className="shrink-0 px-6 pb-3"
        >
          <Text level="small" color="muted">
            {description}
          </Text>
        </ClampedText>
      )}
      {notice}
      {/* Stretched: lattice's column default (items-start) shrinks a view to its content. */}
      <Flex
        direction="column"
        align="stretch"
        flex="1"
        position="relative"
        overflow="hidden"
        paddingX={4}
        className="min-h-0 self-stretch pb-4"
      >
        {children}
      </Flex>
    </Flex>
  );
}
